import { supabase } from './supabase';
import type { AdminUser } from './types';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

function resetInactivityTimer(onTimeout: () => void) {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(onTimeout, INACTIVITY_TIMEOUT);
}

export function startInactivityWatch(onTimeout: () => void) {
  const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
  const handler = () => resetInactivityTimer(onTimeout);
  events.forEach((e) => window.addEventListener(e, handler));
  resetInactivityTimer(onTimeout);
  return () => {
    events.forEach((e) => window.removeEventListener(e, handler));
    if (inactivityTimer) clearTimeout(inactivityTimer);
  };
}

export async function adminLogin(email: string, password: string): Promise<AdminUser> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
  if (!data.user) throw new Error('Login failed');

  const { data: profile, error: profileErr } = await supabase
    .from('profiles')
    .select('id, email, name, role, created_at')
    .eq('id', data.user.id)
    .maybeSingle();
  if (profileErr) throw new Error(profileErr.message);
  if (!profile) throw new Error('Profile not found');
  if (profile.role !== 'admin' && profile.role !== 'editor') {
    throw new Error('Access denied: insufficient permissions');
  }
  return profile as unknown as AdminUser;
}

export async function adminSignup(email: string, password: string, name: string): Promise<AdminUser> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name } },
  });
  if (error) throw new Error(error.message);
  if (!data.user) throw new Error('Signup failed');

  const { data: profile, error: profileErr } = await supabase
    .from('profiles')
    .select('id, email, name, role, created_at')
    .eq('id', data.user.id)
    .maybeSingle();
  if (profileErr) throw new Error(profileErr.message);
  return profile as unknown as AdminUser;
}

export async function adminLogout() {
  await supabase.auth.signOut();
}

export async function getCurrentAdmin(): Promise<AdminUser | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  const { data: profile } = await supabase
    .from('profiles')
    .select('id, email, name, role, created_at')
    .eq('id', session.user.id)
    .maybeSingle();
  return (profile as unknown as AdminUser) ?? null;
}
